import { TELEMETRY_LIMITS } from './telemetry.types';
import type { TelemetryBatch, TelemetryEvent } from './telemetry.types';

export type TelemetryBatchMeta = Omit<TelemetryBatch, 'sessionId' | 'events'>;

export interface TelemetrySendResult {
    ok: boolean;
    status: number;
    code?: string;
    /** El servidor pide dejar de enviar: la persona se opuso a que se registre su uso. */
    stop?: boolean;
}

export type TelemetrySend = (body: string, keepalive: boolean) => Promise<TelemetrySendResult>;

interface Pending {
    sessionId: string;
    event: TelemetryEvent;
}

/**
 * Cola de eventos pendientes de enviar. Agrupa por sesión (un lote es de una sola sesión: un
 * cierre por inactividad viaja con el id viejo aunque ya haya otra abierta), trocea al tamaño de
 * lote y reintenta con espera creciente si el backend no contesta.
 *
 * Un rechazo del servidor (4xx, o `InvalidBatch`) descarta el lote: reintentarlo daría lo mismo.
 * Un fallo de red o un 5xx lo devuelve a la cola, acotada a `maxQueue`.
 */
export class TelemetryQueue {
    private pending: Pending[] = [];
    private meta = new Map<string, TelemetryBatchMeta>();
    private inFlight = false;
    private failures = 0;
    private nextAttemptAt = 0;

    /** `onStop` se llama una vez si el servidor pide dejar de enviar. */
    constructor (private send: TelemetrySend, private onStop: () => void = () => undefined) {}
    private stopped = false;

    get size (): number {
        return this.pending.length;
    }

    /**
     * Los datos de la sesión se completan con cada evento: el primero puede llegar antes de que el
     * router resuelva la ruta de entrada (la app llama al backend al arrancar), y sin esto la sesión
     * se quedaría sin `entryRoute`. Lo que ya se sabía no se pisa.
     */
    push (sessionId: string, event: TelemetryEvent, meta?: TelemetryBatchMeta): void {
        if (meta) this.meta.set(sessionId, fillMissing(this.meta.get(sessionId), meta));
        if (this.stopped) return;
        this.pending.push({ sessionId, event });

        const excess = this.pending.length - TELEMETRY_LIMITS.maxQueue;
        if (excess > 0) this.pending.splice(0, excess);
    }

    /**
     * Envía lo pendiente. Con `keepalive` —la pestaña se está yendo— manda todo sin esperar
     * respuesta, que no llegaría a leerse, y sin respetar la espera por fallos.
     */
    async flush (token: string | null, { keepalive = false, now = Date.now() } = {}): Promise<void> {
        if (this.stopped || !this.pending.length) return;
        if (!keepalive && (this.inFlight || now < this.nextAttemptAt)) return;

        const chunks = this.chunk(this.pending.splice(0));

        if (keepalive) {
            for (const chunk of chunks) this.send(this.body(chunk, token), true).catch(() => undefined);
            return;
        }

        this.inFlight = true;
        try {
            for (let i = 0; i < chunks.length; i++) {
                if (this.stopped) return;
                const delivered = await this.deliver(chunks[i], token);
                if (!delivered) {
                    this.requeue(chunks.slice(i));
                    this.failures++;
                    this.nextAttemptAt = now + Math.min(TELEMETRY_LIMITS.flushIntervalMs * 2 ** this.failures, TELEMETRY_LIMITS.maxBackoffMs);
                    return;
                }
            }
            this.failures = 0;
            this.nextAttemptAt = 0;
        } finally {
            this.inFlight = false;
        }
    }

    /** `true` si el lote no debe volver a la cola: entró, o el servidor lo rechazó por malo. */
    private async deliver (chunk: Pending[], token: string | null): Promise<boolean> {
        try {
            const result = await this.send(this.body(chunk, token), false);
            if (result.stop) this.stop();
            return result.ok || (result.status >= 400 && result.status < 500) || result.code === 'InvalidBatch';
        } catch {
            return false;
        }
    }

    private stop (): void {
        if (this.stopped) return;
        this.stopped = true;
        this.pending = [];
        this.onStop();
    }

    private body (chunk: Pending[], token: string | null): string {
        const sessionId = chunk[0].sessionId;
        const batch: TelemetryBatch = { sessionId, ...this.meta.get(sessionId), events: chunk.map(p => p.event) };
        return JSON.stringify(token ? [batch, token] : [batch]);
    }

    private chunk (items: Pending[]): Pending[][] {
        const bySession = new Map<string, Pending[]>();
        for (const item of items) {
            const list = bySession.get(item.sessionId) ?? [];
            list.push(item);
            bySession.set(item.sessionId, list);
        }

        const chunks: Pending[][] = [];
        for (const list of bySession.values()) {
            for (let i = 0; i < list.length; i += TELEMETRY_LIMITS.maxBatch) chunks.push(list.slice(i, i + TELEMETRY_LIMITS.maxBatch));
        }
        return chunks;
    }

    private requeue (chunks: Pending[][]): void {
        this.pending.unshift(...chunks.flat());
        const excess = this.pending.length - TELEMETRY_LIMITS.maxQueue;
        if (excess > 0) this.pending.splice(0, excess);
    }
}

function fillMissing (known: TelemetryBatchMeta | undefined, next: TelemetryBatchMeta): TelemetryBatchMeta {
    if (!known) return { ...next };
    const merged = { ...known };
    for (const [key, value] of Object.entries(next)) {
        if (merged[key] === undefined && value !== undefined) merged[key] = value;
    }
    return merged;
}

/**
 * Envía con `fetch` y `text/plain`: es una petición simple, sin preflight de CORS, y con
 * `keepalive` sobrevive al cierre de la pestaña. No pasa por `HTTPProxy`, que es donde se mide
 * cada llamada al backend: la telemetría no se mide a sí misma.
 */
export function fetchTelemetrySend (url: string): TelemetrySend {
    return async (body, keepalive) => {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body,
            keepalive,
            credentials: 'omit',
        });

        let code: string | undefined;
        let stop = false;
        try {
            const json = await response.json();
            if (response.ok) stop = json?.result?.stop === true;
            else code = json?.code;
        } catch { /* sin cuerpo */ }
        return { ok: response.ok, status: response.status, code, stop };
    };
}
